// Rekenkern voor het slepen van panelen en kolommen.
//
// Pure functies: geen React, geen DOM. De sleephaken in components/HomeV2/lib.js
// doen de events, dit bestand doet de GRENZEN en de OPSLAGVORM. Zo is de
// wiskunde met `node --test` te controleren zonder browser, dezelfde afspraak
// als lib/table-widths.mjs.
//
// Opslag gaat sinds deze ronde door een envelop met een versienummer. De oude,
// ongeversioneerde vormen (een kale float voor een verdeling, een kaal object
// voor kolombreedtes) blijven bij het LEZEN geldig: een gebruiker mag zijn
// bestaande indeling niet kwijtraken door een upgrade. Geschreven wordt er
// alleen nog in de envelop.

export const SPLIT_GUTTER = 16;      // de middelste track waar het handvat in zit
export const SPLIT_MIN_PANE = 340;   // px, geen van beide kolommen mag hieronder zakken
export const SPLIT_MIN_RATIO = 0.25;
export const SPLIT_MAX_RATIO = 0.75;

// `avail` is de ruimte die de twee fr-tracks samen krijgen: containerbreedte min
// padding min de gutter. Onder 2x minPane kan de px-grens niet gehaald worden,
// dan geldt alleen de verhoudingsgrens.
export function clampSplit(v, avail, opts = {}) {
  const minRatio = Number.isFinite(opts.minRatio) ? opts.minRatio : SPLIT_MIN_RATIO;
  const maxRatio = Number.isFinite(opts.maxRatio) ? opts.maxRatio : SPLIT_MAX_RATIO;
  const minPane = Number.isFinite(opts.minPane) ? opts.minPane : SPLIT_MIN_PANE;
  let lo = minRatio;
  let hi = maxRatio;
  const a = Number(avail);
  if (Number.isFinite(a) && a >= minPane * 2) {
    lo = Math.max(lo, minPane / a);
    hi = Math.min(hi, 1 - minPane / a);
  }
  // Een onmeetbare waarde (NaN uit een kapotte opslag of een deling door nul)
  // mag nooit als breedte de layout in: die valt terug op het midden.
  const x = Number(v);
  if (!Number.isFinite(x)) return Math.min(hi, Math.max(lo, 0.5));
  return Math.min(hi, Math.max(lo, x));
}

// Toetsenbordstap op de verdeling: 2 procentpunt per pijltoets, daarna dezelfde
// grenzen als bij het slepen. Afgerond op drie decimalen, net als de sleep, zodat
// opslag en scherm niet uiteenlopen.
export function nudgeRatio(ratio, dir, avail, opts = {}) {
  const step = 0.02 * (Number(dir) || 0);
  const base = Number.isFinite(Number(ratio)) ? Number(ratio) : 0.5;
  return Math.round(clampSplit(base + step, avail, opts) * 1000) / 1000;
}

// Automatisch passend maken: de breedste gemeten celinhoud plus wat lucht,
// binnen dezelfde onder- en bovengrens als een handmatige sleep. Geen enkele
// meetbare cel -> null, dan laat de aanroeper de breedte staan.
export function autofitFromWidths(cellScrollWidths, opts = {}) {
  const min = Number.isFinite(opts.min) ? opts.min : 56;
  const max = Number.isFinite(opts.max) ? opts.max : 560;
  const pad = Number.isFinite(opts.pad) ? opts.pad : 13;
  const list = Array.isArray(cellScrollWidths) ? cellScrollWidths : [];
  let widest = -Infinity;
  for (let i = 0; i < list.length; i += 1) {
    const w = Number(list[i]);
    if (Number.isFinite(w) && w > widest) widest = w;
  }
  if (widest === -Infinity) return null;
  return Math.min(max, Math.max(min, Math.round(widest + pad)));
}

const NUMERIC = /^-?(?:\d+\.?\d*|\.\d+)$/;

function asObject(x) {
  return x && typeof x === 'object' && !Array.isArray(x) ? x : null;
}

// Alleen eindige getallen als kolombreedte: een null of een string uit een oude
// opslag zou verderop als `width: "abc"` de tabel in lekken.
function cleanWidths(obj) {
  const out = {};
  let any = false;
  Object.keys(obj).forEach((k) => {
    const n = Number(obj[k]);
    if (Number.isFinite(n)) { out[k] = n; any = true; }
  });
  return any ? out : null;
}

// raw = de ruwe localStorage-string. Geeft de genormaliseerde envelop terug, of
// null bij rommel (kapotte JSON, verkeerd soort, lege opslag).
export function parseStored(raw, kind) {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const txt = raw.trim();
  let parsed;
  try { parsed = JSON.parse(txt); } catch { parsed = undefined; }

  if (kind === 'split') {
    const o = asObject(parsed);
    if (o && o.v === 1 && Number.isFinite(Number(o.ratio))) {
      return { v: 1, ratio: Number(o.ratio), collapsed: !!o.collapsed };
    }
    // Legacy: een kale float als string ('0.62').
    if (NUMERIC.test(txt)) {
      const f = Number(txt);
      if (Number.isFinite(f)) return { v: 1, ratio: f, collapsed: false };
    }
    return null;
  }

  if (kind === 'colw') {
    const o = asObject(parsed);
    if (!o) return null;
    if (o.v === 1) {
      const w = asObject(o.w);
      const c = w ? cleanWidths(w) : null;
      return c ? { v: 1, w: c } : null;
    }
    // Legacy: een kaal { kolomsleutel: px }-object.
    const c = cleanWidths(o);
    return c ? { v: 1, w: c } : null;
  }

  if (kind === 'pane') {
    const o = asObject(parsed);
    if (o && o.v === 1 && Number.isFinite(Number(o.width))) {
      return { v: 1, width: Number(o.width), collapsed: !!o.collapsed };
    }
    return null; // geen oude vorm: panelen waren tot nu toe hardgecodeerd
  }

  return null;
}

// Geeft de JSON-string die naar localStorage gaat, of null bij een onbekend soort.
export function serializeStored(kind, data) {
  const d = asObject(data) || {};
  if (kind === 'split') {
    return JSON.stringify({ v: 1, ratio: Number(d.ratio) || 0, collapsed: !!d.collapsed });
  }
  if (kind === 'colw') {
    return JSON.stringify({ v: 1, w: asObject(d.w) || {} });
  }
  if (kind === 'pane') {
    return JSON.stringify({ v: 1, width: Number(d.width) || 0, collapsed: !!d.collapsed });
  }
  return null;
}

// ── Eenmalige, versiegebonden overzetting van een opgeslagen verdeling ───────
// Eigenaar 4 okt 2026: "70/30 de standaard, een keer terugzetten voor iedereen, daarna
// telt het eigen slepen weer". De nieuwe standaard kwam alleen bij een browser ZONDER
// opgeslagen verdeling aan; wie ooit sleepte, of de oude gedeelde sleutel droeg (die
// seedRecordSplit per soort kopieert), bleef op 50/50 staan. Deze overzetting draait
// een keer per sleutel: staat er een leesbare verdeling die afwijkt van de doelwaarde,
// dan gaat de oude waarde ongewijzigd naar de terugrolsleutel en komt de doelwaarde op
// de sleutel. Daarna staat de versiemarkering op de sleutel, zodat een latere eigen
// sleepactie van de gebruiker nooit meer wordt overschreven. Puur: `storage` is elk
// object met getItem/setItem (localStorage of een nepopslag in een test).
export const SPLIT_LAYOUT_VERSION = 2;
export const splitLayoutVersionKey = (key) => `${key}.layout`;
export const splitRollbackKey = (key, version = SPLIT_LAYOUT_VERSION) => `${key}.before-v${version}`;
/**
 * @returns {'migrated'|'kept'|'empty'|'done'} migrated (waarde vervangen, oude waarde op de
 * terugrolsleutel), kept (stond al op de doelwaarde), empty (niets of onleesbaar
 * opgeslagen), done (deze versie was al toegepast, ook na een latere eigen sleepactie).
 */
export function migrateSplitStorage(storage, key, targetRatio, version = SPLIT_LAYOUT_VERSION, { seedFrom = [] } = {}) {
  if (!storage || typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function') return 'empty';
  const target = Number(targetRatio);
  if (!Number.isFinite(target)) return 'empty';
  const vKey = splitLayoutVersionKey(key);
  if (Number(storage.getItem(vKey) || 0) >= version) return 'done';
  // Eerste contact met deze sleutel: een oudere sleutel (soortsleutel zonder gebruiker,
  // daarvoor de gedeelde sleutel) wordt een keer overgenomen. Nooit daarna: een
  // dubbelklik-reset wist de sleutel bewust, en zou anders de oude 50/50 terughalen.
  if (storage.getItem(key) == null) {
    for (const from of seedFrom) {
      if (!from || from === key) continue;
      const v = storage.getItem(from);
      if (v != null) { storage.setItem(key, v); break; }
    }
  }
  const raw = storage.getItem(key);
  const saved = parseStored(raw, 'split');
  let outcome = 'empty';
  if (saved && Number.isFinite(saved.ratio)) {
    if (Math.abs(saved.ratio - target) > 1e-9) {
      storage.setItem(splitRollbackKey(key, version), String(raw));
      storage.setItem(key, serializeStored('split', { ratio: target, collapsed: !!saved.collapsed }));
      outcome = 'migrated';
    } else outcome = 'kept';
  }
  storage.setItem(vKey, String(version));
  return outcome;
}
